import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getDiscussionTopics, getComments, postComment } from '../../services/api';
import toast from 'react-hot-toast';
import { Users, MessageSquare, Clock, TrendingUp, ExternalLink, Send, X } from 'lucide-react';
// @ts-ignore
import Spinner from '../../components/Spinner';

interface DiscussionTopic {
  id: string;
  topic: string;
  summary?: string;
  timestamp?: any;
  comment_count?: number;
}

interface Comment {
  id: string;
  user_id: string;
  user_info?: {
    displayName?: string;
    photoURL?: string;
  };
  content: string;
  timestamp: any;
}

const CommunityHubView: React.FC = () => {
  const { currentUser } = useAuth();
  const [topics, setTopics] = useState<DiscussionTopic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState<DiscussionTopic | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [newTopicContent, setNewTopicContent] = useState('');
  const [isCreatingTopic, setIsCreatingTopic] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadTopics();
  }, []);

  const loadTopics = async () => {
    setIsLoading(true);
    try {
      const data = await getDiscussionTopics();
      // Fetch full content for each topic
      const topicsWithContent = await Promise.all(
        (data.topics || []).map(async (topic: DiscussionTopic) => {
          try {
            if (currentUser) {
              const token = await currentUser.getIdToken();
              const response = await fetch(`/api/summaries/${topic.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (response.ok) {
                const fullData = await response.json();
                return {
                  ...topic,
                  summary: fullData.summary || fullData.content || fullData.text || topic.summary
                };
              }
            }
          } catch (err) {
            console.error(`Failed to load content for ${topic.id}:`, err);
          }
          return topic;
        })
      );
      setTopics(topicsWithContent);
    } catch (error: any) {
      toast.error(`Could not load discussion topics: ${error.message}`);
      setTopics([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadComments = async (topicId: string) => {
    setIsLoadingComments(true);
    try {
      const data = await getComments('summary', topicId);
      setComments(data.comments || []);
    } catch (error: any) {
      toast.error(`Could not load comments: ${error.message}`);
      setComments([]);
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleCreateTopic = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTopicTitle.trim() || !newTopicContent.trim() || !currentUser) return;

    setIsCreatingTopic(true);
    const loadingToast = toast.loading('Creating topic...');

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/summaries', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: newTopicTitle.trim(),
          summary: newTopicContent.trim(),
          is_public: true
        })
      });

      if (!response.ok) throw new Error('Failed to create topic');
      
      toast.success('Topic created!', { id: loadingToast });
      setShowCreateModal(false);
      setNewTopicTitle('');
      setNewTopicContent('');
      await loadTopics();
    } catch (error: any) {
      toast.error(`Failed to create topic: ${error.message}`, { id: loadingToast });
    } finally {
      setIsCreatingTopic(false);
    }
  };

  const handlePostComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !currentUser || !selectedTopic) return;

    setIsPostingComment(true);
    try {
      const token = await currentUser.getIdToken();
      const commentData = {
        user_id: currentUser.uid,
        entity_type: 'summary',
        entity_id: selectedTopic.id,
        content: newComment.trim(),
      };

      await postComment(token, commentData);
      toast.success('Comment posted!');
      setNewComment('');
      await loadComments(selectedTopic.id);
    } catch (error: any) {
      toast.error(`Failed to post comment: ${error.message}`);
    } finally {
      setIsPostingComment(false);
    }
  };

  const openTopic = async (topic: DiscussionTopic) => {
    setSelectedTopic(topic);
    
    // Fetch full summary content with authentication
    if (!topic.summary && currentUser) {
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(`/api/summaries/${topic.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          // Update topic with full summary content
          const updatedTopic = {
            ...topic,
            summary: data.summary || data.content || data.text || 'No content available'
          };
          setSelectedTopic(updatedTopic);
        } else {
          console.error('Failed to fetch summary:', response.status, await response.text());
        }
      } catch (error) {
        console.error('Failed to fetch full topic details:', error);
      }
    }
    
    await loadComments(topic.id);
  };

  const closeTopic = () => {
    setSelectedTopic(null);
    setComments([]);
    setNewComment('');
  };

  const formatTimeAgo = (timestamp: any) => {
    if (!timestamp) return 'Recently';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
      
      if (seconds < 60) return 'Just now';
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
      return `${Math.floor(seconds / 86400)}d ago`;
    } catch {
      return 'Recently';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      {/* Talkoru Banner */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 flex items-center justify-between shadow-md">
        <div className="flex items-center gap-3">
          <Users size={20} />
          <span className="text-sm font-medium">
            💡 Want expert insights? Connect with <strong>67+ AI personas</strong> on Talkoru!
          </span>
        </div>
        <a
          href="https://www.talkoru.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-bold transition-colors"
        >
          Visit Talkoru <ExternalLink size={14} />
        </a>
      </div>

      {/* Header */}
      <div className="p-6 md:p-8">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Users className="text-blue-600" size={32} />
              Community Discussion Board
            </h1>
            <p className="text-slate-600 mt-2">
              Join conversations on trending topics in finance and investing
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-2 whitespace-nowrap"
          >
            <MessageSquare size={20} />
            New Topic
          </button>
        </div>

        {/* Topics List */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          {topics.length > 0 ? (
            <div className="divide-y divide-slate-200">
              {topics.map((topic) => (
                <div
                  key={topic.id}
                  className="p-6 hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => openTopic(topic)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-slate-900 mb-2 hover:text-blue-600 transition-colors">
                        {topic.topic}
                      </h3>
                      {topic.summary && (
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                          {topic.summary}
                        </p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <div className="flex items-center gap-1">
                          <Clock size={14} />
                          <span>{formatTimeAgo(topic.timestamp)}</span>
                        </div>
                        {topic.comment_count !== undefined && (
                          <div className="flex items-center gap-1">
                            <MessageSquare size={14} />
                            <span>{topic.comment_count} comments</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <TrendingUp size={20} className="text-blue-600" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <MessageSquare size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-bold text-slate-700 mb-2">No Topics Yet</h3>
              <p className="text-sm text-slate-500 mb-4">
                Be the first to start a discussion! Share insights from news articles or market analysis.
              </p>
              <button
                onClick={loadTopics}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-sm"
              >
                Refresh Topics
              </button>
            </div>
          )}
        </div>

        {/* Topic Detail Modal with Comments */}
        {selectedTopic && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={closeTopic}
          >
            <div
              className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
                <h2 className="text-xl font-bold text-slate-900 flex-1 pr-4">
                  {selectedTopic.topic}
                </h2>
                <button
                  onClick={closeTopic}
                  className="text-slate-400 hover:text-slate-600 transition-colors p-1 hover:bg-slate-100 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>
              
              {/* Modal Body - Scrollable */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Summary - Show whatever content we have */}
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  {selectedTopic.summary ? (
                    <div className="prose max-w-none">
                      <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{selectedTopic.summary}</p>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-slate-500">Loading full content...</p>
                    </div>
                  )}
                </div>
                
                {/* Comments Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <MessageSquare size={20} />
                    Comments ({comments.length})
                  </h3>
                  
                  {isLoadingComments ? (
                    <div className="flex items-center justify-center py-8">
                      <Spinner />
                    </div>
                  ) : comments.length > 0 ? (
                    <div className="space-y-4">
                      {comments.map((comment) => {
                        const userName = comment.user_info?.displayName || 'Anonymous';
                        const avatarUrl = comment.user_info?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=random`;
                        
                        return (
                          <div key={comment.id} className="bg-white border border-slate-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                              <img
                                src={avatarUrl}
                                alt={userName}
                                className="w-10 h-10 rounded-full"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-bold text-slate-900">{userName}</span>
                                  <span className="text-xs text-slate-500">{formatTimeAgo(comment.timestamp)}</span>
                                </div>
                                <p className="text-slate-700 leading-relaxed">{comment.content}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <MessageSquare size={40} className="mx-auto text-slate-300 mb-2" />
                      <p className="text-slate-500">No comments yet. Be the first to comment!</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Comment Input - Sticky Footer */}
              <div className="border-t border-slate-200 p-4 bg-slate-50 rounded-b-xl">
                <form onSubmit={handlePostComment} className="flex gap-3">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    disabled={isPostingComment}
                    className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                  />
                  <button
                    type="submit"
                    disabled={isPostingComment || !newComment.trim()}
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold transition-all ${
                      isPostingComment || !newComment.trim()
                        ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                    }`}
                  >
                    <Send size={16} />
                    {isPostingComment ? 'Posting...' : 'Post'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Create Topic Modal */}
        {showCreateModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => !isCreatingTopic && setShowCreateModal(false)}
          >
            <div
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-900">Create New Topic</h2>
                <button
                  onClick={() => !isCreatingTopic && setShowCreateModal(false)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                  disabled={isCreatingTopic}
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleCreateTopic} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={newTopicTitle}
                    onChange={(e) => setNewTopicTitle(e.target.value)}
                    placeholder="What's your topic about?"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isCreatingTopic}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Content *
                  </label>
                  <textarea
                    value={newTopicContent}
                    onChange={(e) => setNewTopicContent(e.target.value)}
                    placeholder="Share your thoughts, analysis, or question..."
                    rows={8}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    disabled={isCreatingTopic}
                    required
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                    disabled={isCreatingTopic}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isCreatingTopic || !newTopicTitle.trim() || !newTopicContent.trim()}
                    className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                      isCreatingTopic || !newTopicTitle.trim() || !newTopicContent.trim()
                        ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                    }`}
                  >
                    {isCreatingTopic ? 'Creating...' : 'Create Topic'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityHubView;
